import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const TARGET_FIELDS = ["external_ref","name","category","description","city","state","address","phone","website","image_url","is_professional","professional_title","verified","featured"];
const REQUIRED = new Set(["name","category","city","state","description"]);

export default function AdminImportPage() {
  const { user } = useAuth(); const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [parseData, setParseData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [matchBy, setMatchBy] = useState("external_ref");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (user && user.role !== "admin") { nav("/"); return null; }

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/import/parse", fd, { headers: {"Content-Type": "multipart/form-data"}});
      setParseData(data); setMapping(data.auto_mapping || {}); setStep(2);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Parse failed"); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/run", { preview_id: parseData.preview_id, mapping, match_by: matchBy, dry_run: true });
      setPreview(data); setStep(3);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/run", { preview_id: parseData.preview_id, mapping, match_by: matchBy, dry_run: false });
      setResult(data); setStep(4); toast.success(`Import done: +${data.created} / ~${data.updated}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed — rolled back"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="admin-import-page">
      <div className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-2">Admin</div>
      <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-8">CSV / Excel Import Wizard</h1>

      <div className="flex gap-2 mb-8 text-xs">
        {["Upload","Map fields","Preview","Done"].map((s,i)=>(
          <div key={s} className={`px-3 py-1.5 rounded-full border ${step>=i+1?"bg-blue-700 text-white border-blue-700":"bg-white text-slate-600 border-slate-200"}`}>{i+1}. {s}</div>
        ))}
      </div>

      {step===1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Upload className="h-10 w-10 text-blue-700 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Upload a CSV or XLSX (max 10MB, 5000 rows).</p>
          <input type="file" accept=".csv,.xlsx" onChange={onFile} disabled={busy} data-testid="import-file-input" />
        </div>
      )}

      {step===2 && parseData && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="mb-4 text-sm text-slate-600">Detected <b>{parseData.total_rows}</b> rows and <b>{parseData.headers.length}</b> columns. Map each target field to a column in your file.</div>
          <div className="mb-6">
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">Match existing listings by</label>
            <Select value={matchBy} onValueChange={setMatchBy}>
              <SelectTrigger className="w-64" data-testid="import-matchby"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="external_ref">Reference number (external_ref)</SelectItem>
                <SelectItem value="name_city">Business name + city</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {TARGET_FIELDS.map(f=>(
              <div key={f} className="flex items-center gap-3">
                <div className="w-44 text-sm text-slate-700">{f}{REQUIRED.has(f)&&<span className="text-red-500">*</span>}</div>
                <Select value={mapping[f]||"__none__"} onValueChange={v=>setMapping({...mapping,[f]:v==="__none__"?"":v})}>
                  <SelectTrigger className="flex-1" data-testid={`map-${f}`}><SelectValue placeholder="— none —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {parseData.headers.map(h=><SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <Button variant="outline" onClick={()=>setStep(1)}>Back</Button>
            <Button onClick={runPreview} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="import-preview-btn">
              Preview <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {step===3 && preview && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[["Create",preview.summary.create,"text-green-700 bg-green-50"],["Update",preview.summary.update,"text-blue-700 bg-blue-50"],["Errors",preview.summary.errors,"text-red-700 bg-red-50"],["Total",preview.summary.total,"text-slate-700 bg-slate-100"]].map(([l,v,c])=>(
              <div key={l} className={`rounded-lg p-4 ${c}`}><div className="text-2xl font-bold">{v}</div><div className="text-xs uppercase tracking-widest mt-1">{l}</div></div>
            ))}
          </div>
          {preview.errors?.length>0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2"><AlertTriangle className="h-4 w-4"/> Row errors (will be skipped)</div>
              <div className="max-h-64 overflow-auto border border-red-200 rounded-lg divide-y divide-red-100 bg-red-50 text-sm">
                {preview.errors.map((e,i)=><div key={i} className="p-2"><b>Row {e.row}:</b> {e.error}</div>)}
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={()=>setStep(2)}>Back</Button>
            <Button onClick={commit} disabled={busy || (preview.summary.create+preview.summary.update)===0} className="bg-blue-700 hover:bg-blue-800" data-testid="import-commit-btn">
              Commit import ({preview.summary.create + preview.summary.update} rows)
            </Button>
          </div>
        </div>
      )}

      {step===4 && result && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4"/>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Import complete</h2>
          <p className="text-slate-600 mb-6">Created <b>{result.created}</b> · Updated <b>{result.updated}</b> · Skipped <b>{result.errors_skipped}</b></p>
          <Button onClick={()=>{setStep(1);setParseData(null);setPreview(null);setResult(null);}} className="bg-blue-700 hover:bg-blue-800">Import another file</Button>
        </div>
      )}
    </div>
  );
}
